"use client";

import React, {
  useState,
  useCallback,
  useRef,
  createContext,
  useContext,
  useEffect,
} from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { SlidersIcon } from "@phosphor-icons/react";
import { Button } from "@tranquility-ai/theme-components/button";
import { useTimePilotT } from "@/i18n/useTimePilotT";
import { TpTooltip } from "@tranquility-ai/theme-components/tooltip";
import {
  SidePanel,
  SidePanelShell,
  SIDE_PANEL_BACKDROP_CLASS,
  SIDE_PANEL_ABOVE_PRIMARY_NAV_BACKDROP_Z_CLASS,
  SIDE_PANEL_ABOVE_PRIMARY_NAV_Z_CLASS,
} from "./side-panel";
import {
  MediaSourceShareProvider,
  useMediaSourceShare,
  type InjectedDrawerApi,
} from "./mediaSourceShare";
import {
  useCopyHistoryState,
  CopyHistoryListener,
  type CopyHistoryState,
} from "./recently-copied";
import { useResearchNotesState } from "./research-notes";
import { useCapturesState } from "./tools-captures";
import FavoritesList from "./favorites-list";
import {
  ToolsSharedCaseDataProvider,
  sharedDataCasesMatch,
  useOptionalToolsSharedCaseData,
} from "./tools-shared-case-data";
import type { MediaSource } from "@/shared/types";
import { AuthContext, FeaturesContext } from "@/app/contexts";

export type ToolsDrawerTabId =
  | "sources"
  | "recently-copied"
  | "research-notes"
  | "captures";

/**
 * React Aria modal overlay (used by HeroUI Drawer) sets overflow/padding on the root while open.
 * If cleanup races Framer exit, the page can stay non-interactive. Only strip those inline styles
 * when no other dialog is still mounted (avoids breaking a real modal open underneath).
 */
function restoreDocumentInteractionIfNoModalsRemain() {
  if (typeof document === "undefined") return;
  if (document.querySelectorAll('[aria-modal="true"]').length > 0) return;
  document.documentElement.style.removeProperty("overflow");
  document.documentElement.style.removeProperty("padding-right");
  document.body.style.removeProperty("margin-top");
}

function scheduleRestoreDocumentInteractionIfNoModalsRemain() {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      restoreDocumentInteractionIfNoModalsRemain();
      setTimeout(restoreDocumentInteractionIfNoModalsRemain, 0);
    });
  });
}

type ToolsDrawerContextValue = {
  isOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  toggleDrawer: () => void;
  onOpenChange: (open: boolean) => void;
  activeTab: ToolsDrawerTabId;
  setActiveTab: (tab: ToolsDrawerTabId) => void;
  openDrawerToTab: (tab: ToolsDrawerTabId) => void;
  caseId: string | undefined;
  copyHistoryState: CopyHistoryState;
  /** For injection into MediaSourceShareProvider (avoids circular dependency). */
  injectDrawerApi: InjectedDrawerApi;
  /**
   * When the event modal is editing media, UnifiedSources registers a handler so the Captures tab
   * can apply a saved capture (S3 URI or legacy data URL) to the current slide.
   */
  registerApplyCaptureToEventMedia: (fn: ((imageRef: string) => void) | null) => void;
  applyCaptureToEventMedia: (imageRef: string) => boolean;
  /** Bumps when a capture was applied from the tools drawer (MediaDisplay uses this to show capture view). */
  captureApplyNonce: number;
  /** True while UnifiedSources (event modal) has registered apply — enables Captures tab "apply to slide". */
  eventMediaCaptureApplyReady: boolean;
  /**
   * When the event (or title) modal is editing sources, UnifiedSources registers this so Sources & Media
   * can push a drawer entry into the modal's selected-sources list (same as search + "Add selected").
   */
  registerAddSharedSourceToEvent: (fn: ((source: MediaSource) => void) | null) => void;
  addSharedSourceToEvent: (source: MediaSource) => boolean;
  eventSharedSourceAddReady: boolean;
  /** Event modal registers this so Research Notes / Recently Copied can set the Description field. */
  registerApplyTextToEventDescription: (fn: ((text: string) => void) | null) => void;
  applyTextToEventDescription: (text: string) => boolean;
  eventDescriptionApplyReady: boolean;
};

const ToolsDrawerContext = createContext<ToolsDrawerContextValue | null>(null);

/** Narrow subscription: only `captureApplyNonce` changes here, not on drawer open/tab/copy history updates. */
const ToolsDrawerCaptureApplyNonceContext = createContext(0);

export function useToolsDrawer() {
  return useContext(ToolsDrawerContext);
}

export function useToolsDrawerCaptureApplyNonce() {
  return useContext(ToolsDrawerCaptureApplyNonceContext);
}

/**
 * Wraps app chrome while the tools drawer is open. Blur is owned by the drawer
 * scrim (`SIDE_PANEL_BACKDROP_CLASS`) rather than a filter on this subtree: the
 * subtree contains the primary nav, and an ancestor `filter` would blur it too.
 */
export function ToolsDrawerChrome({ children }: { children: React.ReactNode }) {
  return <div>{children}</div>;
}

function ToolsDrawerShell({ caseId }: { caseId: string | undefined }) {
  const t = useTimePilotT();
  const drawer = useToolsDrawer();
  const mediaShare = useMediaSourceShare();
  const sharedCase = useOptionalToolsSharedCaseData();
  const { featureFlags } = useContext(FeaturesContext) || {};
  const researchNotesState = useResearchNotesState(caseId);
  const capturesState = useCapturesState(caseId);
  const drawerCaptures =
    capturesState.captures.length > 0
      ? capturesState.captures
      : sharedCase?.hydrated &&
          caseId &&
          sharedDataCasesMatch(caseId, sharedCase.caseId, sharedCase.tenantFriendlyName)
        ? sharedCase.captures
        : [];
  const showCapturesInDrawer =
    !!featureFlags?.capture ||
    drawerCaptures.length > 0 ||
    capturesState.apiActive;
  const [mounted, setMounted] = useState(false);
  const [hasSelection, setHasSelection] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [isFiltered, setIsFiltered] = useState(false);

  const handleCountsChange = useCallback((_total: number, _filtered: number, filtered: boolean) => {
    setIsFiltered(filtered);
  }, []);

  const isOpen = drawer?.isOpen ?? false;
  const closeDrawer = drawer?.closeDrawer;
  const copyHistoryState = drawer?.copyHistoryState;
  const reloadRemoteBundle =
    caseId &&
    sharedCase &&
    sharedDataCasesMatch(caseId, sharedCase.caseId, sharedCase.tenantFriendlyName)
      ? sharedCase.refetch
      : null;
  const refetchCopyHistory = copyHistoryState?.refetch;

  /** Latest refetch fns — hook return objects may be recreated each render; deps must stay stable. */
  const reloadRemoteBundleRef = useRef(reloadRemoteBundle);
  const refetchCopyHistoryRef = useRef(refetchCopyHistory);
  const mediaRefetchEntriesRef = useRef(mediaShare?.refetchEntries);
  const researchNotesRefetchRef = useRef(researchNotesState.refetch);
  const capturesRefetchRef = useRef(capturesState.refetch);
  reloadRemoteBundleRef.current = reloadRemoteBundle;
  refetchCopyHistoryRef.current = refetchCopyHistory;
  mediaRefetchEntriesRef.current = mediaShare?.refetchEntries;
  researchNotesRefetchRef.current = researchNotesState.refetch;
  capturesRefetchRef.current = capturesState.refetch;

  useEffect(() => {
    setMounted(true);
  }, []);

  /** Remote case: one shared bundle; refetch when the drawer opens only (not on tab change — avoids racing debounced persist). */
  useEffect(() => {
    if (!isOpen || !refetchCopyHistoryRef.current) return;
    if (!reloadRemoteBundleRef.current) return;
    void reloadRemoteBundleRef.current();
  }, [isOpen, caseId, !!reloadRemoteBundle]);

  /** Local storage: refresh all data when the drawer opens. */
  useEffect(() => {
    if (!isOpen || !refetchCopyHistoryRef.current) return;
    if (reloadRemoteBundleRef.current) return;

    mediaRefetchEntriesRef.current?.();
    refetchCopyHistoryRef.current();
    researchNotesRefetchRef.current();
    capturesRefetchRef.current();
  }, [isOpen, !!reloadRemoteBundle]);

  useEffect(() => {
    if (!isOpen || !closeDrawer) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      // HeroUI / React Aria modals set aria-modal="true"; let them handle Escape first.
      if (document.querySelector('[aria-modal="true"]')) return;
      e.preventDefault();
      closeDrawer();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, closeDrawer]);

  /** Open/close can interact with other overlays; keep document styles consistent. */
  useEffect(() => {
    scheduleRestoreDocumentInteractionIfNoModalsRemain();
  }, [isOpen]);

  if (!drawer || !closeDrawer || !copyHistoryState) return null;

  if (!mounted) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            key="drawer-backdrop"
            aria-hidden
            className={`${SIDE_PANEL_BACKDROP_CLASS} ${SIDE_PANEL_ABOVE_PRIMARY_NAV_BACKDROP_Z_CLASS} bg-black/20`}
            initial={{ opacity: 0, backdropFilter: "blur(0px)", WebkitBackdropFilter: "blur(0px)" }}
            animate={{ opacity: 1, backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }}
            exit={{ opacity: 0, backdropFilter: "blur(0px)", WebkitBackdropFilter: "blur(0px)" }}
            transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
            onPointerDown={() => closeDrawer()}
          />
          <SidePanelShell
            key="drawer-panel"
            side="right"
            aria-modal={false}
            aria-label={t("favorites.title")}
            className={`${SIDE_PANEL_ABOVE_PRIMARY_NAV_Z_CLASS} transition-[max-width,min-width] duration-200 ease-in-out max-w-[95vw] min-w-[95vw] ${hasSelection ? "md:max-w-[50vw] md:min-w-[50vw]" : "sm:max-w-[330px] sm:min-w-[330px]"}`}
          >
            <SidePanel
              side="right"
              title={t("favorites.title")}
              className="min-h-0 flex-1"
              onCollapse={closeDrawer}
              collapseLabel={t("favorites.closePanel")}
              headerActions={
                <TpTooltip content={t("favorites.toggleFilters")} delayDuration={0} sideOffset={4}>
                  <Button
                    type="button"
                    iconOnly
                    size="sm"
                    style={showFilters ? "solid" : "text"}
                    color="light"
                    background="inverse"
                    onClick={() => setShowFilters((p) => !p)}
                    aria-label={t("favorites.toggleFilters")}
                  >
                    <SlidersIcon weight={showFilters || isFiltered ? "fill" : "regular"} />
                  </Button>
                </TpTooltip>
              }
            >
              <FavoritesList
                entries={mediaShare?.entries ?? []}
                setEntries={mediaShare?.setEntries ?? (() => {})}
                persistEntries={mediaShare?.persistEntries ?? (() => {})}
                caseId={mediaShare?.caseId}
                onAddToTimeline={mediaShare?.onAddToTimeline}
                addSharedSourceToEvent={drawer.addSharedSourceToEvent}
                eventSharedSourceAddReady={drawer.eventSharedSourceAddReady}
                notes={researchNotesState.notes}
                onAddNote={researchNotesState.addNote}
                onUpdateNote={researchNotesState.updateNote}
                onDeleteNote={researchNotesState.deleteNote}
                copyHistory={copyHistoryState.copyHistory}
                copiedEntryId={copyHistoryState.copiedEntryId}
                onCopyEntry={copyHistoryState.handleCopyEntryClick}
                onRemoveCopyEntry={copyHistoryState.handleRemoveEntry}
                captures={drawerCaptures}
                onRemoveCapture={capturesState.removeCapture}
                captureEnabled={showCapturesInDrawer}
                applyTextToEventDescription={drawer.applyTextToEventDescription}
                eventDescriptionApplyReady={drawer.eventDescriptionApplyReady}
                showFilters={showFilters}
                onSelectionChange={setHasSelection}
                onCountsChange={handleCountsChange}
              />
            </SidePanel>
          </SidePanelShell>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
}

export function ToolsDrawerProvider({
  children,
  caseId,
  tenantFriendlyName: tenantFriendlyNameProp,
  cellIdFromInit,
  onAddToTimeline,
}: {
  children: React.ReactNode;
  caseId?: string;
  /** Tenant friendly name for shared-data partition; defaults to AuthContext.tenantName. */
  tenantFriendlyName?: string | null;
  /** Fallback cell id from ``/api/init`` (meta-viewer / standalone pages). */
  cellIdFromInit?: string | null;
  onAddToTimeline?: (event: import("./answer-item").EventFromChat) => void;
}) {
  const auth = useContext(AuthContext);
  const tenantFriendlyName =
    (tenantFriendlyNameProp ?? auth.tenantName ?? "").trim() || null;

  return (
    <ToolsSharedCaseDataProvider
      caseId={caseId}
      tenantFriendlyName={tenantFriendlyName}
      cellIdFromInit={cellIdFromInit}
    >
      <ToolsDrawerProviderInner caseId={caseId} onAddToTimeline={onAddToTimeline}>
        {children}
      </ToolsDrawerProviderInner>
    </ToolsSharedCaseDataProvider>
  );
}

/** Must render under ToolsSharedCaseDataProvider so copy history can load/persist via sharedData API. */
function ToolsDrawerProviderInner({
  children,
  caseId,
  onAddToTimeline,
}: {
  children: React.ReactNode;
  caseId?: string;
  onAddToTimeline?: (event: import("./answer-item").EventFromChat) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTabState] = useState<ToolsDrawerTabId>("sources");
  const copyHistoryState = useCopyHistoryState(caseId);

  const openDrawer = useCallback(() => setIsOpen(true), []);
  const closeDrawer = useCallback(() => setIsOpen(false), []);
  const toggleDrawer = useCallback(() => setIsOpen((prev) => !prev), []);
  const onOpenChange = useCallback((open: boolean) => setIsOpen(open), []);

  const openDrawerToTab = useCallback((tab: ToolsDrawerTabId) => {
    setActiveTabState(tab);
    setIsOpen(true);
  }, []);

  const setActiveTab = useCallback((tab: ToolsDrawerTabId) => {
    setActiveTabState(tab);
  }, []);

  const applyCaptureToEventMediaRef = useRef<((imageRef: string) => void) | null>(null);
  const [captureApplyNonce, setCaptureApplyNonce] = useState(0);
  const [eventMediaCaptureApplyReady, setEventMediaCaptureApplyReady] = useState(false);

  const registerApplyCaptureToEventMedia = useCallback(
    (fn: ((imageRef: string) => void) | null) => {
      applyCaptureToEventMediaRef.current = fn;
      setEventMediaCaptureApplyReady(fn != null);
    },
    [],
  );

  const applyCaptureToEventMedia = useCallback((imageRef: string) => {
    const fn = applyCaptureToEventMediaRef.current;
    if (!fn) return false;
    fn(imageRef);
    setCaptureApplyNonce((n) => n + 1);
    return true;
  }, []);

  const addSharedSourceToEventRef = useRef<((source: MediaSource) => void) | null>(null);
  const [eventSharedSourceAddReady, setEventSharedSourceAddReady] = useState(false);

  const registerAddSharedSourceToEvent = useCallback(
    (fn: ((source: MediaSource) => void) | null) => {
      addSharedSourceToEventRef.current = fn;
      setEventSharedSourceAddReady(fn != null);
    },
    [],
  );

  const addSharedSourceToEvent = useCallback((source: MediaSource) => {
    const fn = addSharedSourceToEventRef.current;
    if (!fn) return false;
    fn(source);
    return true;
  }, []);

  const applyTextToEventDescriptionRef = useRef<((text: string) => void) | null>(null);
  const [eventDescriptionApplyReady, setEventDescriptionApplyReady] = useState(false);

  const registerApplyTextToEventDescription = useCallback(
    (fn: ((text: string) => void) | null) => {
      applyTextToEventDescriptionRef.current = fn;
      setEventDescriptionApplyReady(fn != null);
    },
    [],
  );

  const applyTextToEventDescription = useCallback((text: string) => {
    const fn = applyTextToEventDescriptionRef.current;
    if (!fn) return false;
    fn(text);
    return true;
  }, []);

  const injectDrawerApi: InjectedDrawerApi = React.useMemo(
    () => ({
      isOpen,
      openDrawer,
      closeDrawer,
      openDrawerToTab,
    }),
    [isOpen, openDrawer, closeDrawer, openDrawerToTab],
  );

  const value: ToolsDrawerContextValue = React.useMemo(
    () => ({
      isOpen,
      openDrawer,
      closeDrawer,
      toggleDrawer,
      onOpenChange,
      activeTab,
      setActiveTab,
      openDrawerToTab,
      caseId,
      copyHistoryState,
      injectDrawerApi,
      registerApplyCaptureToEventMedia,
      applyCaptureToEventMedia,
      captureApplyNonce,
      eventMediaCaptureApplyReady,
      registerAddSharedSourceToEvent,
      addSharedSourceToEvent,
      eventSharedSourceAddReady,
      registerApplyTextToEventDescription,
      applyTextToEventDescription,
      eventDescriptionApplyReady,
    }),
    [
      isOpen,
      openDrawer,
      closeDrawer,
      toggleDrawer,
      onOpenChange,
      activeTab,
      setActiveTab,
      openDrawerToTab,
      caseId,
      copyHistoryState,
      injectDrawerApi,
      registerApplyCaptureToEventMedia,
      applyCaptureToEventMedia,
      captureApplyNonce,
      eventMediaCaptureApplyReady,
      registerAddSharedSourceToEvent,
      addSharedSourceToEvent,
      eventSharedSourceAddReady,
      registerApplyTextToEventDescription,
      applyTextToEventDescription,
      eventDescriptionApplyReady,
    ],
  );

  const onAddToTimelineAndCloseDrawer = useCallback((event: import("./answer-item").EventFromChat) => {
    if (onAddToTimeline) {
      onAddToTimeline(event);
      closeDrawer();
    }
  }, [onAddToTimeline, closeDrawer]);

  return (
    <ToolsDrawerCaptureApplyNonceContext.Provider value={captureApplyNonce}>
      <ToolsDrawerContext.Provider value={value}>
        <CopyHistoryListener addToCopyHistory={copyHistoryState.addToCopyHistory} />
        <MediaSourceShareProviderWithDrawer
          caseId={caseId}
          onAddToTimeline={onAddToTimelineAndCloseDrawer}
          injectDrawerApi={injectDrawerApi}
        >
          {children}
          <ToolsDrawerShell caseId={caseId} />
        </MediaSourceShareProviderWithDrawer>
      </ToolsDrawerContext.Provider>
    </ToolsDrawerCaptureApplyNonceContext.Provider>
  );
}

function MediaSourceShareProviderWithDrawer({
  children,
  caseId,
  onAddToTimeline,
  injectDrawerApi,
}: {
  children: React.ReactNode;
  caseId?: string;
  onAddToTimeline?: (event: import("./answer-item").EventFromChat) => void;
  injectDrawerApi: InjectedDrawerApi;
}) {
  return (
    <MediaSourceShareProvider
      caseId={caseId}
      onAddToTimeline={onAddToTimeline}
      injectDrawer={injectDrawerApi}
    >
      {children}
    </MediaSourceShareProvider>
  );
}
